import firebase from 'firebase/compat/app';
import 'firebase/compat/database';
import 'firebase/compat/auth';
import 'firebase/compat/storage';
import { ImageProcessor } from '../elements/katapult-upload-page/ImageProcessor.js';
import { GetExif } from '../modules/GetExif.js';

/* global File */
// Since this is a worker used by both mobile and desktop we are just using firebase.database() instead of FirebaseWorker

export class UploadWorker {
  constructor() {
    this._queue = [];
    this._retryAttempts = {};
    this._restorePhotos = [];
    self.addEventListener('message', async (e) => {
      if (e.data.call) var res = await this[e.data.call].apply(this, e.data.args);
      if (e.data.promise) self.postMessage({ type: 'promiseResponse', promiseKey: e.data.promise, res });
    });
  }

  init(firebaseConfig, online, options, onProgress, onComplete) {
    options = options || {};
    this.parallelCount = options.parallelCount || 2;
    this.isMobile = options.isMobile;
    this.online = online;
    if (firebase.apps.length == 0) {
      firebase.initializeApp(firebaseConfig);
      // This call to auth should not be necessary but is included to prevent a bug
      // where any firebase RTDB calls in a worker result in a permission denied error.
      // This should be removed when we upgrade to the modular version of firebase.
      firebase.auth();
    }
    if (onProgress) this._onprogress = onProgress;
    if (onComplete) this._oncomplete = onComplete;
  }

  async onlineChanged(online) {
    this.online = online;
    if (this.online) {
      await this._restorePhotosToQueue();
      this._processPhotos();
    }
  }

  /* Arguments
    fileList:
      - a FileList
    options:
      jobId: The jobId of all items.
      folderId - The folderId for the photos (will be created if not already on job)
      cameraId - The cameraId for the photos (will be created if not already on job)
      uid - The uploading user's uid
    */

  async uploadFromFiles(fileList, options, promiseKey) {
    options = options || {};
    let optionProperties = ['jobId', 'folderId', 'cameraId', 'uid'];
    if (!optionProperties.every((p) => options[p] != null)) {
      throw new Error(
        'Upload From Files requires: ' + optionProperties.join(', ') + ' but only received: "' + JSON.stringify(options) + '"'
      );
    }
    let filesArray = UploadWorker._toFilesArray(fileList);
    let photoRecords = await this._toPhotosRecordArray(filesArray, options, promiseKey);
    this.upload(photoRecords);
  }

  async uploadFromFilesArray(filesArray, options, promiseKey) {
    options = options || {};
    let optionProperties = ['jobId', 'folderId', 'cameraId', 'uid'];
    if (!optionProperties.every((p) => options[p] != null)) {
      throw new Error(
        'Upload From Files requires: ' + optionProperties.join(', ') + ' but only received: "' + JSON.stringify(options) + '"'
      );
    }
    let photoRecords = await this._toPhotosRecordArray(filesArray, options, promiseKey);
    this.upload(photoRecords);
  }

  upload(photoRecords) {
    this.active = true;
    this._setupProcessors();
    this._queue.push(...photoRecords);
    this._processPhotos();
  }

  async checkForExistingPhoto(jobId, photoNameDate, photoId) {
    let photos = null;
    if (photoId)
      photos = {
        [photoId]: await firebase
          .database()
          .ref('photoheight/jobs/' + jobId + '/photos/' + photoId)
          .once('value')
          .then((s) => s.val())
      };
    else
      photos = await firebase
        .database()
        .ref('photoheight/jobs/' + jobId + '/photos/')
        .orderByChild('name_date')
        .equalTo(photoNameDate)
        .once('value')
        .then((s) => s.val());
    let existingKey = null;
    let uploadComplete = false;
    let uploadActive = false;
    let existingTag = null;
    let existingCameraId = null;
    if (photos != null) {
      for (let photoId in photos) {
        existingTag = photos[photoId].tags;
        existingCameraId = photos[photoId].camera_id;
        existingKey = photoId;
        if (photos[photoId].status == 'upload_complete') {
          uploadComplete = true;
        } else if (photos[photoId].status == 'upload_active') {
          uploadActive = true;
        }
      }
    }
    return { uploadComplete, uploadActive, existingTag, existingCameraId, existingKey };
  }

  static _toFilesArray(fileList) {
    // Takes FileList object and returns Array of jpeg files.
    let fileKeys = [];
    for (let key in File.prototype) fileKeys.push(key);
    return Array.from(fileList)
      .filter((file) => file.type === 'image/jpeg')
      .map((file) => {
        let temp = { file };
        for (let key in file) if (!fileKeys.includes(key)) temp[key] = file[key];
        return temp;
      });
  }

  async _toPhotosRecordArray(filesArray, options, promiseKey) {
    /* Arguments
    filesArray:
      - An Array of objects with file properties which are File objects of type image/jpeg
      - If options.jobId is null, each item must have a _jobId property
    options:
      jobId: - The jobId of all items.  If null, _jobId property must be on each item.
      folderId - The folderId for the photos (will be created if not already on job)
      cameraId - The cameraId for the photos (will be created if not already on job)
      uid - The uploading user's uid
    */
    options = options || {};
    if (options.addToDashboard == null) options.addToDashboard = false;
    let res = [];
    let update = {};

    // Create folder if non-existant.
    let folder = await firebase
      .database()
      .ref(`photoheight/jobs/${options.jobId}/photo_folders/${options.folderId}`)
      .once('value')
      .then((s) => s.val());
    let newFolder = false;
    let newCamera = false;
    if (!folder) {
      newFolder = true;
      folder = {
        numPhotos: 0,
        numUploaded: 0,
        numTagged: 0,
        numAssociated: 0,
        date_created: firebase.database.ServerValue.TIMESTAMP,
        cameras: {
          [options.cameraId]: {
            numPhotos: 0,
            numUploaded: 0,
            numTagged: 0,
            numAssociated: 0,
            date_created: firebase.database.ServerValue.TIMESTAMP,
            collectionDetails: {}
          }
        },
        done: !options.addToDashboard,
        job_id: options.jobId,
        status: 'Uploading'
      };
      update[`/jobs/${options.jobId}/photo_folders/${options.folderId}`] = folder;
    } else if (!folder[options.cameraId]) {
      newCamera = false;
      // Create camera if non-existant.
      folder[options.cameraId] = {
        numPhotos: 0,
        numUploaded: 0,
        numTagged: 0,
        numAssociated: 0,
        date_created: firebase.database.ServerValue.TIMESTAMP,
        collectionDetails: {}
      };
      update[`jobs/${options.jobId}/photo_folders/${options.folderId}/cameras/${options.cameraId}`] = folder[options.cameraId];
    }

    for (let fileObject of filesArray) {
      let exif = await GetExif(fileObject.file);
      exif.camera_id = options.cameraId;
      exif.folder_id = options.folderId;
      delete exif.rotation; //rotation is handled by the upload script
      exif.uploaded_by = options.uid;
      exif.status = 'upload_started';
      exif['.priority'] = '' + options.cameraId + exif.date_taken;

      let _jobId = options.jobId || fileObject._jobId;
      let status = await this.checkForExistingPhoto(_jobId, exif.name_date, fileObject._photoId);
      let _key = status.existingKey || this._uuid();
      let photo = {
        item: fileObject.file,
        exif,
        status,
        _jobId,
        _key,
        _folderId: options.folderId,
        _cameraId: options.cameraId,
        promiseKey
      };
      if (status.uploadComplete) {
        this._notify('progress', photo.promiseKey, { status: 'complete', photo, message: exif.filename + ' already uploaded' });
      } else {
        res.push(photo);

        // Check if the photo already existed or not. If not, upload the entire
        // exif as new data.  If the photo did exist, then loop through the
        // keys in the exif object and just update them so we don't overwrite
        // the photofirst_data. The reason we don't always loop over the exif
        // keys is that the .priority key can't be individually set on a non-existent object
        if (status.existingKey) {
          for (let exifKey in exif) {
            if (exif.hasOwnProperty(exifKey)) {
              update[`jobs/${_jobId}/photos/${_key}/${exifKey}`] = exif[exifKey];
            }
          }
        } else {
          update['jobs/' + _jobId + '/photos/' + _key] = exif;
        }

        update['jobs/' + _jobId + '/photo_summary/' + _key] = {
          '.priority': exif['.priority'],
          name: exif.filename,
          camera_id: options.cameraId,
          status: 'upload_started'
        };
      }
    }

    if (newFolder) {
      folder.numPhotos += res.length;
    } else {
      await firebase
        .database()
        .ref(`photoheight/jobs/${options.jobId}/photo_folders/${options.folderId}/numPhotos`)
        .transaction((counter) => {
          return (counter || 0) + res.length;
        });
    }

    if (newCamera) {
      [options.cameraId].numPhotos += res.length;
    } else {
      await firebase
        .database()
        .ref(`photoheight/jobs/${options.jobId}/photo_folders/${options.folderId}/cameras/${options.cameraId}/numPhotos`)
        .transaction((counter) => {
          return (counter || 0) + res.length;
        });
    }
    await firebase.database().ref(`photoheight`).update(update);
    let sharing = await firebase
      .database()
      .ref(`photoheight/jobs/${options.jobId}/sharing`)
      .once('value')
      .then((s) => s.val());
    for (let companyId in sharing) {
      await firebase
        .database()
        .ref(`photoheight/counters/${companyId}/${options.jobId}/numPhotos`)
        .transaction((counter) => {
          counter = counter || { value: 0 };
          counter.value += res.length;
          counter.last_modified = firebase.database.ServerValue.TIMESTAMP;
          return counter;
        });
    }

    return res;
  }

  _setupProcessors() {
    if (this._processors) return;
    this._processors = [];
    this._activeProcessors = [];
    let count = this.parallelCount;
    while (count > 0) {
      this._processors.push(new ImageProcessor(this._upload.bind(this)));
      count--;
    }
  }

  _processPhotos(lastProcessedPromiseKey) {
    if (!this._processors || !this._activeProcessors || !this.online) return;
    if (this._queue.length == 0 && this._activeProcessors.length == 0 && this.active) {
      this.active = false;
      this._notify('complete', lastProcessedPromiseKey);
    }
    while (this._queue.length > 0 && this._processors.length > 0) {
      let processor = this._processors.pop();
      this._activeProcessors.push(processor);
      let photo = this._queue.shift();
      this._processPhoto(photo, processor);
    }
  }

  async _processPhoto(photo, processor) {
    let alreadyUploaded = await this._checkUploadStatus(photo);
    if (alreadyUploaded) {
      this._notify('progress', photo.promiseKey, {
        status: 'skipped',
        photo,
        message: photo.exif.filename + ' skipped. Already uploaded on a different computer.'
      });
      this._recycleProcessor(processor, photo.promiseKey);
    } else {
      try {
        let files = await processor.convert(photo, this.isMobile);
        await this._upload(files, photo);
        this._notify('progress', photo.promiseKey, { status: 'complete', photo });
        this._recycleProcessor(processor, photo.promiseKey);
      } catch (e) {
        console.error(e);
        if (!this.online || this._retryAttempts[photo._key] < 3) {
          if (this.online) this._retryAttempts[photo._key] = (this._retryAttempts[photo._key] || 0) + 1;
          this._restorePhotos.push({ photo, processor });
          await this._restorePhotosToQueue();
        } else if (this.online) {
          this._notify('progress', photo.promiseKey, { status: 'failed', photo, message: photo.exif.filename + ' failed to upload.' });

          // Clear status check on photo.
          let update = {};
          update['photos/' + photo._key + '/status_check'] = null;
          update['photos/' + photo._key + '/status'] = 'upload_error';
          update['photos/' + photo._key + '/upload_date'] = null;
          update['photo_summary/' + photo._key + '/status'] = 'upload_error';
          firebase
            .database()
            .ref('photoheight/jobs/' + photo._jobId)
            .update(update);
          // Recycle Processor.
          this._recycleProcessor(processor, photo.promiseKey);
        }
      }
    }
  }

  async _checkUploadStatus(photo) {
    let jobRef = firebase.database().ref('photoheight/jobs/' + photo._jobId);
    let statusCheckKey = jobRef.push().key;
    return jobRef
      .child('/photos/' + photo._key + '/status_check/' + statusCheckKey)
      .set(firebase.database.ServerValue.TIMESTAMP)
      .then(() => {
        return jobRef
          .child('/photos/' + photo._key)
          .once('value')
          .then((s) => {
            let photoData = s.val();
            let firstTime = Infinity;
            for (let key in photoData.status_check) {
              if (photoData.status_check[key] < firstTime) {
                firstTime = photoData.status_check[key];
              }
            }
            let uploadActiveOrCompleted =
              (photoData.status_check != null &&
                photoData.status_check[statusCheckKey] != firstTime &&
                firstTime + 20000 > photoData.status_check[statusCheckKey]) ||
              (photoData.status == 'upload_active' && !photo.status.uploadActive) ||
              photoData.status == 'upload_complete';
            if (uploadActiveOrCompleted) {
              return true;
            } else {
              let update = {};
              update['photos/' + photo._key + '/status_check'] = null;
              update['photos/' + photo._key + '/status'] = 'upload_active';
              update['photos/' + photo._key + '/upload_date'] = firebase.database.ServerValue.TIMESTAMP;
              update['photo_summary/' + photo._key + '/status'] = 'upload_active';
              return jobRef.update(update).then(() => {
                return false;
              });
            }
          });
      });
  }

  async _upload(convertedFiles, photo, processor) {
    let uploads = [];
    let uploadsComplete = 0;
    const totalFilesToUpload = Object.keys(convertedFiles).length;
    // if there are only 2 files to upload, then we are uploading a single photo (the other file is exif data)
    const isSinglePhotoUpload = totalFilesToUpload === 2;
    // TODO (01-25-2023): Some day we will use this to add user group metadata on photos
    //   Also, putting this here is wasteful, as it will be called for each unique photo
    // const userGroup = (await firebase.auth().currentUser.getIdTokenResult())?.claims?.userGroup;
    // const uploadMetadata = { customMetadata: { userGroup } };

    for (const type in convertedFiles) {
      const name = `${photo._key}_${type}`;
      const fileRef = firebase.storage().ref(`photos/${name}`);
      const uploadPromise = fileRef.put(convertedFiles[type]); // fileRef.put(convertedFiles[type], uploadMetadata);
      let stateChangedCallbacks = {
        error: (error) => {
          // Handle unsuccessful uploads
          throw error;
        }
      };
      // if we are uploading a single photo then we want to notify progress for that photo only
      // otherwise we want to notify progress for each photo when it completes
      if (isSinglePhotoUpload && type.includes('full')) {
        stateChangedCallbacks['next'] = (snapshot) => {
          this._notify('progress', photo.promiseKey, {
            status: 'uploading',
            photo,
            progress: Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          });
        };
      } else if (!isSinglePhotoUpload) {
        stateChangedCallbacks['complete'] = () => {
          uploadsComplete++;
          this._notify('progress', photo.promiseKey, {
            status: 'uploading',
            photo,
            progress: Math.round((uploadsComplete / totalFilesToUpload) * 100)
          });
        };
      }
      uploadPromise.on('state_changed', stateChangedCallbacks);
      uploads.push(uploadPromise);
    }

    return Promise.all(uploads)
      .then(async () => {
        let update = {};
        update[`photos/${photo._key}/status`] = 'upload_complete';
        update[`photo_summary/${photo._key}/status`] = null;
        if (photo.addToDashboard) {
          update[`photo_folders/${photo._folderId}/done`] = false;
        } else {
          update[`photo_folders/${photo._folderId}/done`] = true;
        }
        update[`photo_folders/${photo._folderId}/last_upload`] = firebase.database.ServerValue.TIMESTAMP;
        update[`photo_folders/${photo._folderId}/cameras/${photo._cameraId}/last_upload`] = firebase.database.ServerValue.TIMESTAMP;
        let updates = [];
        updates.push(firebase.database().ref(`photoheight/jobs/${photo._jobId}`).update(update));
        updates.push(
          firebase
            .database()
            .ref(`photoheight/jobs/${photo._jobId}/photo_folders/${photo._folderId}/numUploaded`)
            .transaction(this._incrementCounter)
        );
        updates.push(
          firebase
            .database()
            .ref(`photoheight/jobs/${photo._jobId}/photo_folders/${photo._folderId}/cameras/${photo._cameraId}/numUploaded`)
            .transaction(this._incrementCounter)
        );
        if (photo.jobId) {
          updates.push(
            firebase
              .database()
              .ref(`photoheight/jobs/${photo._jobId}/photo_folders/${photo._folderId}/numTagged`)
              .transaction(this._incrementCounter)
          );
          updates.push(
            firebase
              .database()
              .ref(`photoheight/jobs/${photo._jobId}/photo_folders/${photo._folderId}/cameras/${photo._cameraId}/numTagged`)
              .transaction(this._incrementCounter)
          );
        }
        updates.push(
          firebase
            .database()
            .ref(`photoheight/jobs/${photo._jobId}/sharing`)
            .once('value')
            .then((s) => {
              let companies = s.val();
              let companyUpdates = [];
              for (let companyId in companies) {
                companyUpdates.push(
                  firebase
                    .database()
                    .ref(`photoheight/counters/${companyId}/${photo._jobId}/numUploaded`)
                    .transaction((counter) => {
                      counter = counter || { value: 0 };
                      counter.value += 1;
                      counter.last_modified = firebase.database.ServerValue.TIMESTAMP;
                      return counter;
                    })
                );
                if (photo.jobId) {
                  companyUpdates.push(
                    firebase
                      .database()
                      .ref(`photoheight/counters/${companyId}/${photo._jobId}/numTagged`)
                      .transaction((counter) => {
                        counter = counter || { value: 0 };
                        counter.value += 1;
                        counter.last_modified = firebase.database.ServerValue.TIMESTAMP;
                        return counter;
                      })
                  );
                }
              }
              return Promise.all(companyUpdates);
            })
        );
        return Promise.all(updates);
      })
      .catch((err) => {
        uploads.forEach((task) => task.cancel());
        throw err;
      });
  }

  _incrementCounter(counter) {
    return (counter || 0) + 1;
  }

  _restorePhotosToQueue() {
    let updates = [];
    while (this.online && this._restorePhotos.length) {
      let temp = this._restorePhotos.pop();

      let update = {};
      update['photos/' + temp.photo._key + '/status_check'] = null;
      update['photos/' + temp.photo._key + '/status'] = 'upload_started';
      update['photos/' + temp.photo._key + '/upload_date'] = null;
      update['photo_summary/' + temp.photo._key + '/status'] = 'upload_started';
      updates.push(
        firebase
          .database()
          .ref('photoheight/jobs/' + temp.photo._jobId)
          .update(update)
          .then(() => {
            this._queue.unshift(temp.photo);
            this._recycleProcessor(temp.processor, temp.photo.promiseKey);
          })
      );
    }
    return Promise.all(updates);
  }

  _recycleProcessor(processor, promiseKey) {
    this._activeProcessors.splice(this._activeProcessors.indexOf(processor), 1);
    this._processors.push(processor);
    this._processPhotos(promiseKey);
  }

  _uuid(a, b) {
    for (b = a = ''; a++ < 36; b += (a * 51) & 52 ? (a ^ 15 ? 8 ^ (Math.random() * (a ^ 20 ? 16 : 4)) : 4).toString(16) : '-');
    return b;
  }

  _notify(type, promiseKey, message) {
    if (this['_on' + type]) this['_on' + type](promiseKey, message);
    else self.postMessage({ type, promiseKey, message });
  }
}

new UploadWorker();
